import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, isSameData,
  isSameList,
} from '../../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { LocalizedRichDescription } from '../localized-rich-description.js';
import { PaymentAccountRef } from '../payment-account-ref.js';
import { Price } from '../price.js';

export class DonationSettings extends DataModel {
  static readonly TYPE_ID: string = '291f0e20b47449f4a3a02b0f976be18c';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly allowDonations: boolean,
    public readonly donationMessage: LocalizedRichDescription | null,
    public readonly donationOptions: Price[],
    public readonly paymentAccount: PaymentAccountRef | null,
  ) {
    super();
  }

  get $typeId(): string {
    return DonationSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, DonationSettings.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    allowDonations: boolean,
    donationMessage: LocalizedRichDescription | null,
    donationOptions: Price[],
    paymentAccount: PaymentAccountRef | null,
  }): DonationSettings {
    return new DonationSettings(
        args.businessRef,
        args.allowDonations,
        args.donationMessage,
        args.donationOptions,
        args.paymentAccount,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DonationSettings {
    return DonationSettings.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      allowDonations: MapUtils.getBoolean(map, 'allowDonations'),
      donationMessage: context.fromDataOrNull<LocalizedRichDescription>(MapUtils.getRecordOrNull(map, 'donationMessage')),
      donationOptions: MapUtils.getList<Price>(map, 'donationOptions', (v) => Price.decode(v as string)),
      paymentAccount: context.fromDataOrNull<PaymentAccountRef>(MapUtils.getRecordOrNull(map, 'paymentAccount')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'allowDonations': this.allowDonations,
      'donationMessage': context.toDataOrNull(this.donationMessage),
      'donationOptions': this.donationOptions.map((e) => e.encode()),
      'paymentAccount': context.toDataOrNull(this.paymentAccount),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    businessRef = cloneData(this.businessRef),
    allowDonations = this.allowDonations,
    donationMessage = cloneDataOrNull(this.donationMessage),
    donationOptions = cloneListOfCloneable(this.donationOptions),
    paymentAccount = cloneDataOrNull(this.paymentAccount),
  }: {
    businessRef?: BusinessModuleRef,
    allowDonations?: boolean,
    donationMessage?: LocalizedRichDescription | null,
    donationOptions?: Price[],
    paymentAccount?: PaymentAccountRef | null,
  } = {}): this {
    return DonationSettings.create({
      businessRef,
      allowDonations,
      donationMessage,
      donationOptions,
      paymentAccount,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.allowDonations === this.allowDonations &&
      isSameData(other.donationMessage, this.donationMessage) &&
      isSameData(other.paymentAccount, this.paymentAccount) &&
      isSameList(other.donationOptions, this.donationOptions, isSameData);
  }
}

export class DonationSettingsBuilder extends DataModelBuilder<DonationSettings> {
  build(context: SerializationContext, map: SerializedData): DonationSettings {
    return DonationSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(DonationSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(DonationSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added a payment account.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
